import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import api, { formatApiError } from "../api";
import { Briefcase, Users, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";

export default function ProsLanding() {
  const [form, setForm] = useState({ nom_complet: "", email: "", profession: "Notaire", cabinet: "", telephone: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setStatus("loading");
    try {
      await api.post("/contact/partner", form);
      setStatus("ok");
    } catch (err) {
      setError(formatApiError(err));
      setStatus("idle");
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--auprea-cream)" }}>
      <nav className="flex items-center justify-between px-6 md:px-12 py-6 border-b" style={{ borderColor: "rgba(21,41,79,0.1)" }}>
        <Link to="/"><Logo size={28} /></Link>
        <Link to="/" className="text-sm" style={{ color: "var(--auprea-blue)" }}>← Retour au site</Link>
      </nav>

      <section className="px-6 md:px-12 lg:px-24 pt-16 pb-16">
        <div className="max-w-5xl mx-auto">
          <div style={{ color: "var(--auprea-gold)", fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 16 }}>
            — Pour les notaires & conseillers en gestion de patrimoine
          </div>
          <h1 style={{ fontSize: "clamp(2.25rem, 5vw, 3.75rem)", lineHeight: 1.1, fontWeight: 400, marginBottom: 24 }}>
            Offrez à vos clients un <em style={{ color: "var(--auprea-gold)", fontStyle: "italic", fontWeight: 300 }}>carnet de transmission</em> qu'ils n'oublieront jamais.
          </h1>
          <p style={{ fontSize: 18, lineHeight: 1.7, maxWidth: 720 }}>
            AUPREA accompagne vos clients entre deux rendez-vous. Ils préparent leur transmission au calme,
            vous récupérez un dossier complet et structuré le jour J — sans paperasse manquante.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16" style={{ backgroundColor: "#ffffff" }}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-px" style={{ backgroundColor: "rgba(21,41,79,0.12)" }}>
          {[
            { icon: <Briefcase size={32} color="#caa86a" />, title: "Gain de temps", text: "Vos clients arrivent en rendez-vous avec un carnet déjà rempli : famille, patrimoine, volontés. Plus de questionnaire en séance." },
            { icon: <ShieldCheck size={32} color="#caa86a" />, title: "Accès en lecture seule", text: "Vos clients vous partagent leur carnet pour la durée que vous fixez ensemble (24h, 7j, 30j). Aucune modification possible de votre côté." },
            { icon: <Users size={32} color="#caa86a" />, title: "Une expérience premium", text: "Style banque privée, données hébergées en France, conforme RGPD. Vos clients vous remercient de la recommandation." },
          ].map((f) => (
            <div key={f.title} className="p-10 bg-white">
              {f.icon}
              <h3 style={{ marginTop: 20, marginBottom: 12, fontSize: 22 }}>{f.title}</h3>
              <p style={{ lineHeight: 1.7 }}>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Formulaire */}
      <section className="px-6 md:px-12 lg:px-24 py-16" id="contact">
        <div className="max-w-2xl mx-auto auprea-card">
          {status === "ok" ? (
            <div className="text-center py-8" data-testid="partner-success">
              <CheckCircle2 size={56} color="#2f7d5b" style={{ margin: "0 auto 16px" }} />
              <h2 style={{ fontSize: 26 }}>Merci, votre demande est bien reçue.</h2>
              <p style={{ marginTop: 12, color: "rgba(35,38,46,0.7)" }}>
                L'équipe AUPREA vous recontactera sous 48h ouvrées pour échanger sur le partenariat.
              </p>
            </div>
          ) : (
            <>
              <div style={{ color: "var(--auprea-gold)", fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
                — Devenir partenaire
              </div>
              <h2 style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", marginTop: 8, marginBottom: 8 }}>Demande de partenariat</h2>
              <p style={{ color: "rgba(35,38,46,0.7)", marginBottom: 24, fontSize: 15 }}>
                Notaires, CGP, family officers — laissez-nous vos coordonnées, nous vous présenterons AUPREA en 30 min.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="auprea-label">Nom complet *</label>
                    <input className="auprea-input" value={form.nom_complet} onChange={(e) => setForm({ ...form, nom_complet: e.target.value })} required data-testid="partner-nom" />
                  </div>
                  <div>
                    <label className="auprea-label">Email professionnel *</label>
                    <input type="email" className="auprea-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="partner-email" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="auprea-label">Profession *</label>
                    <select className="auprea-input" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} data-testid="partner-profession">
                      <option>Notaire</option>
                      <option>Conseiller en gestion de patrimoine (CGP)</option>
                      <option>Family office</option>
                      <option>Avocat en droit patrimonial</option>
                      <option>Expert-comptable</option>
                      <option>Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="auprea-label">Cabinet / Société</label>
                    <input className="auprea-input" value={form.cabinet} onChange={(e) => setForm({ ...form, cabinet: e.target.value })} data-testid="partner-cabinet" />
                  </div>
                </div>
                <div>
                  <label className="auprea-label">Téléphone</label>
                  <input className="auprea-input" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="partner-tel" />
                </div>
                <div>
                  <label className="auprea-label">Votre message</label>
                  <textarea className="auprea-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="partner-msg" />
                </div>
                {error && <div style={{ color: "#b53a3a", fontSize: 14, padding: "10px 12px", background: "#fcf3f3", border: "1px solid #f0c5c5" }} data-testid="partner-error">{error}</div>}
                <button type="submit" className="auprea-btn w-full" disabled={status === "loading"} data-testid="partner-submit">
                  {status === "loading" ? "Envoi…" : "Envoyer ma demande"} <ArrowRight size={16} />
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
